/**
 * @fileoverview Location Preview Map Component
 * @description Shows a map with a marker at the selected location, optionally with a radius circle.
 */

import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import type { Map as LeafletMap } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

// Fix default marker icon issue
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })
  ._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
});

// ============================================================================
// TYPES
// ============================================================================

interface LocationPreviewMapProps {
  latitude: number;
  longitude: number;
  /** Radius in kilometers — shown as a translucent circle */
  radiusKm?: number;
  className?: string;
  height?: string;
}

// ============================================================================
// MAP UPDATER COMPONENT
// ============================================================================

function MapUpdater({
  latitude,
  longitude,
  radiusKm,
}: {
  latitude: number;
  longitude: number;
  radiusKm?: number;
}) {
  const map = useMap();

  useEffect(() => {
    if (radiusKm && radiusKm > 0) {
      // Fit the map to show the full circle
      const radiusMeters = radiusKm * 1000;
      const center = L.latLng(latitude, longitude);
      const bounds = center.toBounds(radiusMeters * 2);
      map.fitBounds(bounds, { padding: [20, 20], animate: true });
    } else {
      map.setView([latitude, longitude], 16, { animate: true });
    }
  }, [map, latitude, longitude, radiusKm]);

  return null;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function LocationPreviewMap({
  latitude,
  longitude,
  radiusKm,
  className = '',
  height = '180px',
}: LocationPreviewMapProps) {
  const mapRef = useRef<LeafletMap | null>(null);

  const center: [number, number] = [latitude, longitude];
  const radiusMeters = radiusKm ? radiusKm * 1000 : undefined;

  return (
    <div
      className={`rounded-lg overflow-hidden border border-input ${className}`}
    >
      <MapContainer
        center={center}
        zoom={16}
        style={{ height, width: '100%' }}
        ref={mapRef}
        scrollWheelZoom={false}
        dragging={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Radius circle */}
        {radiusMeters && (
          <Circle
            center={center}
            radius={radiusMeters}
            pathOptions={{
              color: '#3B82F6',
              fillColor: '#3B82F6',
              fillOpacity: 0.12,
              weight: 2,
            }}
          />
        )}

        {/* Location marker */}
        <Marker position={center} />

        {/* Map updater to center on location changes */}
        <MapUpdater
          latitude={latitude}
          longitude={longitude}
          radiusKm={radiusKm}
        />
      </MapContainer>
    </div>
  );
}

export default LocationPreviewMap;
